import { Button, ProgressCircle, Text } from '@react-spectrum/s2';
import ChevronLeft from '@react-spectrum/s2/icons/ChevronLeft';
import { useEffect } from 'react';

import { trackEvent } from '@/utils/fullstory';

import { StrategyDetailView } from './StrategyDetailView';
import { StrategyFailedView, StrategyRunView } from './StrategyRunView';
import { useStrategyDetail } from './useStrategies';
import { useStrategyActions } from './useStrategyActions';

// Detail switch for one strategy id: RunView while generating, FailedView on failure,
// StrategyDetailView when complete. The ONE place a single run is fetched (useStrategyDetail).
// Each strategy stands alone — no siblings, no variant switcher.
export function StrategyDetail({
	id,
	onBack,
	onDeleted,
	onGenerateAgain,
	onRegenerate,
	onViewSetup,
}: {
	id: string;
	onBack: () => void;
	onDeleted: () => void;
	// The COMPLETE view's header control: start a fresh batch beside a plan that already worked.
	onGenerateAgain: () => void | Promise<void>;
	// The FAILED view's control: rebuild this one dead variant in place. Separate from
	// `onGenerateAgain` because the two are different asks, and the route refuses a run that is
	// not dead — pointing the complete view here would answer a working control with a 409.
	onRegenerate: (strategyId: string) => Promise<void>;
	onViewSetup: (processId: string) => void;
}) {
	const { run, events, notFound, error, refresh } = useStrategyDetail(id);
	const { remove, materializeCampaign } = useStrategyActions();

	const complete = run?.status === 'complete';
	useEffect(() => {
		if (complete) trackEvent('Strategy Completed Viewed', { strategy_id: id });
	}, [complete, id]);

	if (notFound) {
		return (
			<div
				className="scrollbar-hidden min-h-0 flex-1 space-y-4 overflow-y-auto
					py-8"
			>
				<p className="text-(--text-muted)">
					This strategy is no longer available.
				</p>
				<Button variant="secondary" onPress={onBack}>
					<ChevronLeft />
					<Text>Back to strategies</Text>
				</Button>
			</div>
		);
	}

	if (error && !run) {
		return (
			<div
				className="flex min-h-0 flex-1 flex-col items-center justify-center
					gap-3 py-16 text-center"
			>
				<p className="text-base font-semibold">{error}</p>
				<Button variant="secondary" onPress={onBack}>
					<ChevronLeft />
					<Text>Back to strategies</Text>
				</Button>
			</div>
		);
	}

	// Full-view spinner only on a TRUE cold load (no run yet). On a sibling hop the prior run
	// stays mounted, so the header/switcher persist and only the body swaps in — no whole-view
	// flash. `loading` alone must not blank the view.
	if (!run) {
		return (
			<div className="flex flex-1 items-center justify-center py-16">
				<ProgressCircle isIndeterminate aria-label="Loading strategy" />
			</div>
		);
	}

	const back = (
		<div className="pb-4">
			<Button variant="secondary" size="S" onPress={onBack}>
				<ChevronLeft />
				<Text>Back</Text>
			</Button>
		</div>
	);

	if (run.status === 'generating') {
		return (
			<div className="flex min-h-0 flex-1 flex-col">
				{back}
				<StrategyRunView events={events} startedAt={run.started_at} />
			</div>
		);
	}

	if (run.status === 'failed') {
		return (
			<div className="flex min-h-0 flex-1 flex-col">
				{back}
				<StrategyFailedView
					error={run.error}
					startedAt={run.started_at}
					onGenerateAgain={() => onRegenerate(id)}
					onDelete={async () => {
						await remove(id);
						onDeleted();
					}}
				/>
			</div>
		);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<StrategyDetailView
				run={run}
				onBack={onBack}
				onDelete={async () => {
					await remove(id);
					onDeleted();
				}}
				onGenerateAgain={onGenerateAgain}
				onActivate={async (sid, cid) => {
					const reply = await materializeCampaign(sid, cid);
					trackEvent('Campaign Activated From Strategy', {
						strategy_id: sid,
						campaign_id: cid,
					});
					// The whole reply through, `status` included — the card needs it to say
					// whether this built the campaign or found it already live.
					return reply;
				}}
				onActivationConflict={refresh}
				onViewSetup={onViewSetup}
			/>
		</div>
	);
}
